import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, startOfWeek } from 'date-fns';
import { dashboardApi, DashboardSummary } from '../services/api';
import { useLocationContext } from '../components/Layout';
import { AlertCircle, Clock, AlertTriangle, Calendar, User, Loader2, UserPlus, Plus, ClipboardList, FileX, GraduationCap, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { usePermissions } from '../hooks/usePermissions';
import { formatCompanyDate } from '../utils/companyDateFormat';

// Helper function to extract timezone-naive time from datetime string
const extractTimezoneNaiveTime = (datetimeString: string | null | undefined): string => {
  if (!datetimeString) return '';
  try {
    const timePart = datetimeString.split('T')[1];
    if (!timePart) return '';
    const [hours, minutes] = timePart.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch (error) {
    console.warn('Error extracting timezone-naive time:', error);
    return '';
  }
};

// Loading spinner component
const LoadingSpinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <Loader2 className={`animate-spin text-gray-400`} size={size} />
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const weekStartsOn = useWeekStartDay();
  const { debouncedSelectedLocationId } = useLocationContext();
  const { hasTaskManagement, hasEmployeeManagement, hasScheduling, hasReports, healthcareComplianceEnabled } = usePermissions();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchSummary = useCallback(async (buttonRefresh = false) => {
    try {
      if (buttonRefresh) {
        setIsRefreshing(true);
      } else {
        setLoadingSummary(true);
      }
      const data = await dashboardApi.getSummary(debouncedSelectedLocationId);
      setSummary(data);
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      if (!buttonRefresh) {
        setSummary(null);
      }
    } finally {
      setLoadingSummary(false);
      setIsRefreshing(false);
    }
  }, [debouncedSelectedLocationId]);

  useEffect(() => {
    void fetchSummary(false);
    const interval = setInterval(() => void fetchSummary(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  const totalEmployeesDisplayCount = useMemo(() => {
    if (!summary) return 0;
    return debouncedSelectedLocationId != null
      ? summary.counts.mainEmployeesAtLocation
      : summary.counts.totalEmployees;
  }, [summary, debouncedSelectedLocationId]);

  const totalEmployeesSubCount = summary?.counts.sharedSubEmployeesCount ?? 0;
  const presentTodayCount = summary?.counts.presentToday ?? 0;
  const pendingHolidayRequestsCount = summary?.counts.pendingHolidayRequests ?? 0;

  const absentAlerts = useMemo(
    () =>
      (summary?.absentToday ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        date: row.date,
      })),
    [summary]
  );

  const missedClockoutAlerts = useMemo(
    () =>
      (summary?.missedClockout ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        record: { id: row.recordId, clockInTime: row.clockInTime },
        date: row.date,
        hoursClocked: row.hoursClocked,
      })),
    [summary]
  );

  const visaAlerts = useMemo(
    () =>
      (summary?.visaAlerts ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        visaNumber: row.visaNumber,
        visaExpiryDate: row.visaExpiryDate,
        daysUntilExpiry: row.daysUntilExpiry,
      })),
    [summary]
  );

  const licenseAlerts = useMemo(
    () =>
      (summary?.licenseExpired ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        license: {
          id: row.licenseId,
          licenseType: row.licenseType,
          licenseNumber: row.licenseNumber,
          expiryDate: row.expiryDate,
          renewalUploaded: row.renewalUploaded,
        },
        daysSinceExpiry: row.daysSinceExpiry,
      })),
    [summary]
  );

  const licenseExpiringSoonAlerts = useMemo(
    () =>
      (summary?.licenseExpiringSoon ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        license: {
          id: row.licenseId,
          licenseType: row.licenseType,
          licenseNumber: row.licenseNumber,
          expiryDate: row.expiryDate,
          renewalUploaded: row.renewalUploaded,
        },
        daysUntilExpiry: row.daysUntilExpiry,
      })),
    [summary]
  );

  const upcomingTraining = useMemo(
    () =>
      (summary?.upcomingTraining ?? []).map((row) => ({
        employee: {
          id: row.employeeId,
          firstName: row.firstName,
          lastName: row.lastName,
        },
        record: { id: row.recordId },
        dueDate: row.dueDate,
        label: row.label,
        isOverdue: row.isOverdue,
      })),
    [summary]
  );

  const loadingPresentToday = loadingSummary;
  const loadingPendingHolidays = loadingSummary;
  const loadingAbsentAlerts = loadingSummary;
  const loadingMissedClockoutAlerts = loadingSummary;
  const loadingVisaAlerts = loadingSummary;
  const loadingLicenseAlerts = loadingSummary;
  const loadingUpcomingTraining = loadingSummary;

  if (loadingSummary && !summary) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="text-center">
          <LoadingSpinner size={32} />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const expiredVisas = visaAlerts.filter((v) => v.daysUntilExpiry < 0);
  const expiringSoonVisas = visaAlerts.filter((v) => v.daysUntilExpiry >= 0 && v.daysUntilExpiry <= 30);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar - Alerts */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
          {/* Absent Alerts */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <h3 className="font-medium text-gray-900">Absent Today</h3>
              {!loadingAbsentAlerts && absentAlerts.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                  {absentAlerts.length}
                </span>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 h-48 overflow-y-auto">
              {loadingAbsentAlerts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2 px-3">
                  <LoadingSpinner size={16} />
                  <span>Loading...</span>
                </div>
              ) : absentAlerts.length > 0 ? (
                <div className="p-2 space-y-2">
                  {absentAlerts.map(({ employee, date }) => (
                    <div
                      key={`absent-${employee.id}-${date}`}
                      className="p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      <div 
                        className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => window.open(`/employees/${employee.id}?tab=notes`, '_blank')}
                        title="View employee notes"
                      >
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {formatCompanyDate(date)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Missed Clockout Alerts */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-shrink-0">
              <Clock className="h-5 w-5 text-red-600" />
              <h3 className="font-medium text-gray-900">Missed Clockout</h3>
              {!loadingMissedClockoutAlerts && missedClockoutAlerts.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                  {missedClockoutAlerts.length}
                </span>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 h-48 overflow-y-auto">
              {loadingMissedClockoutAlerts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2 px-3">
                  <LoadingSpinner size={16} />
                  <span>Loading...</span>
                </div>
              ) : missedClockoutAlerts.length > 0 ? (
                <div className="p-2 space-y-2">
                  {missedClockoutAlerts.map(({ employee, record, date, hoursClocked }) => {
                    const clockInTime = extractTimezoneNaiveTime(record.clockInTime);
                    const dateStr = format(new Date(date), 'yyyy-MM-dd');
                    const recordDate = new Date(date);
                    const weekStartDate = startOfWeek(recordDate, { weekStartsOn });
                    const weekStr = format(weekStartDate, 'yyyy-MM-dd');
                    return (
                      <div
                        key={`missed-${record.id}`}
                        onClick={() => window.open(`/attendance?week=${weekStr}&date=${dateStr}&recordId=${record.id}`, '_blank')}
                        className="p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                      >
                        <div className="font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Clocked in: {clockInTime} on {formatCompanyDate(date)}
                        </div>
                        <div className="text-xs text-red-700 font-medium mt-1">
                          Hours clocked: {hoursClocked} (24+ hours - missed clock out)
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full max-w-[82%] mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-neutral-800">Dashboard</h1>
          <button
            type="button"
            onClick={() => void fetchSummary(true)}
            disabled={isRefreshing || loadingSummary}
            title="Refresh dashboard"
            aria-label="Refresh dashboard"
            className="p-2 rounded-lg text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-neutral-600 mt-2">Welcome to your employee management dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Total Employees</p>
              {loadingSummary ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <>
                  <p className="text-3xl font-bold text-neutral-800">
                    {totalEmployeesDisplayCount}
                    {debouncedSelectedLocationId != null && totalEmployeesSubCount > 0 && (
                      <span className="text-lg font-normal text-neutral-500 ml-1">
                        + {totalEmployeesSubCount} shared
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
            <div className="text-3xl">👥</div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Present Today</p>
              {loadingPresentToday ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-neutral-800">
                  {presentTodayCount}
                </p>
              )}
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Pending Holiday Requests</p>
              {loadingPendingHolidays ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-neutral-800">
                  {pendingHolidayRequestsCount}
                </p>
              )}
            </div>
            <div className="text-3xl">📅</div>
          </div>
        </div>
      </div>

      {/* Row 2: Quick Actions only */}
      <div className="mb-8">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {hasEmployeeManagement && (
                <button
                  onClick={() => navigate('/employees')}
                  className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-neutral-800">Add Employee</div>
                    <div className="text-sm text-neutral-600 mt-0.5">Create a new employee profile</div>
                  </div>
                </button>
              )}

              {hasScheduling && (
                <button
                  onClick={() => navigate('/scheduling')}
                  className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Plus className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-neutral-800">Add Shift</div>
                    <div className="text-sm text-neutral-600 mt-0.5">Schedule a new shift</div>
                  </div>
                </button>
              )}

              <button
                onClick={() => navigate('/reports/timecards-early-late')}
                className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left group ${
                  hasReports ? 'hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasReports}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Early/Late Report</div>
                  <div className="text-sm text-neutral-600 mt-0.5">View early and late arrivals</div>
                </div>
              </button>
            </div>

            <div className="space-y-4">
              {hasTaskManagement && (
                <button
                  onClick={() => navigate('/task-admin')}
                  className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-neutral-800">View Tasks</div>
                    <div className="text-sm text-neutral-600 mt-0.5">Check pending tasks</div>
                  </div>
                </button>
              )}

              <button
                onClick={() => navigate('/reports/plan-vs-actual')}
                className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left group ${
                  hasReports ? 'hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasReports}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">View Labour</div>
                  <div className="text-sm text-neutral-600 mt-0.5">Check labour costs and hours</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/reports/timecards-break')}
                className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left group ${
                  hasReports ? 'hover:bg-blue-50' : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!hasReports}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Break Report</div>
                  <div className="text-sm text-neutral-600 mt-0.5">View break time reports</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Alerts - Expired Licenses, Expired Visas, Upcoming Training */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Compliance / Licenses: Expired + Expiring within 30 days */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <FileX className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-neutral-800 text-sm">
              {healthcareComplianceEnabled ? 'Compliance & Renewals' : 'Licenses'}
            </h3>
            {!loadingLicenseAlerts && (licenseAlerts.length > 0 || licenseExpiringSoonAlerts.length > 0) && (
              <span className="px-2 py-1 text-xs font-medium bg-neutral-600 text-white rounded-full">
                {licenseAlerts.length + licenseExpiringSoonAlerts.length}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 max-h-96 pr-2">
            {loadingLicenseAlerts ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
                <LoadingSpinner size={16} />
                <span>Loading...</span>
              </div>
            ) : (
              <>
                {licenseAlerts.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-red-700 mb-1.5">Expired</p>
                    <div className="space-y-2">
                      {licenseAlerts.map(({ employee, license }) => (
                        <div
                          key={`license-exp-${employee.id}-${license.id}`}
                          onClick={() => window.open(`/employees/${employee.id}?tab=licenses`, '_blank')}
                          className="p-2 bg-red-50 rounded border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {license.licenseType ? `${license.licenseType} · ` : ''}
                                {license.licenseNumber} • Expired {formatCompanyDate(license.expiryDate)}
                                {license.renewalUploaded && ' · Renewal uploaded'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {licenseExpiringSoonAlerts.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1.5">Expiring within 30 days</p>
                    <div className="space-y-2">
                      {licenseExpiringSoonAlerts.map(({ employee, license, daysUntilExpiry }) => (
                        <div
                          key={`license-soon-${employee.id}-${license.id}`}
                          onClick={() => window.open(`/employees/${employee.id}?tab=licenses`, '_blank')}
                          className="p-2 bg-amber-50 rounded border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-amber-800 mt-1">
                                {license.licenseType ? `${license.licenseType} · ` : ''}
                                {license.licenseNumber} • Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''} • {formatCompanyDate(license.expiryDate)}
                                {license.renewalUploaded && ' · Renewal uploaded'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {licenseAlerts.length === 0 && licenseExpiringSoonAlerts.length === 0 && (
                  <p className="text-sm text-neutral-500 py-2">No expired or expiring licenses</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Visas: Expired + Expiring within 30 days */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-neutral-800 text-sm">Visas</h3>
            {!loadingVisaAlerts && (expiredVisas.length > 0 || expiringSoonVisas.length > 0) && (
              <span className="px-2 py-1 text-xs font-medium bg-neutral-600 text-white rounded-full">
                {expiredVisas.length + expiringSoonVisas.length}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 max-h-96 pr-2">
            {loadingVisaAlerts ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
                <LoadingSpinner size={16} />
                <span>Loading...</span>
              </div>
            ) : (
              <>
                {expiredVisas.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-red-700 mb-1.5">Expired</p>
                    <div className="space-y-2">
                      {expiredVisas.map(({ employee, visaNumber, visaExpiryDate, daysUntilExpiry }) => (
                        <div
                          key={`visa-expired-${employee.id}-${visaNumber}`}
                          onClick={() => window.open(`/employees/${employee.id}?tab=personal&scrollToVisa=true#visa-details`, '_blank')}
                          className="p-2 bg-red-50 rounded border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {visaNumber && `(${visaNumber}) `}
                                Expired {Math.abs(daysUntilExpiry)} day{Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} ago • {formatCompanyDate(visaExpiryDate)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expiringSoonVisas.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1.5">Expiring within 30 days</p>
                    <div className="space-y-2">
                      {expiringSoonVisas.map(({ employee, visaNumber, visaExpiryDate, daysUntilExpiry }) => (
                        <div
                          key={`visa-soon-${employee.id}-${visaNumber}`}
                          onClick={() => window.open(`/employees/${employee.id}?tab=personal&scrollToVisa=true#visa-details`, '_blank')}
                          className="p-2 bg-amber-50 rounded border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {employee.firstName} {employee.lastName}
                              </div>
                              <div className="text-xs text-amber-800 mt-1 line-clamp-2">
                                {visaNumber && `(${visaNumber}) `}
                                {daysUntilExpiry === 0 ? 'Expires today' : daysUntilExpiry === 1 ? 'Expires tomorrow' : `Expires in ${daysUntilExpiry} days`} • {formatCompanyDate(visaExpiryDate)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {expiredVisas.length === 0 && expiringSoonVisas.length === 0 && (
                  <p className="text-sm text-neutral-500 py-2">No expired or expiring visas</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Upcoming & Overdue Training (for selected location) */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <GraduationCap className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-neutral-800 text-sm">Upcoming & Overdue Training</h3>
            {!loadingUpcomingTraining && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded-full">
                {upcomingTraining.length}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 max-h-96">
            {loadingUpcomingTraining ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
                <LoadingSpinner size={16} />
                <span>Loading...</span>
              </div>
            ) : upcomingTraining.length > 0 ? (
              <div className="space-y-2 pr-2">
                {upcomingTraining.map(({ employee, record, dueDate, label, isOverdue }, idx) => (
                  <div
                    key={`upcoming-${record.id}-${idx}-${dueDate}`}
                    onClick={() => window.open(`/employees/${employee.id}?tab=training`, '_blank')}
                    className={`p-2 rounded border cursor-pointer transition-colors ${
                      isOverdue
                        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                        : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className={`text-xs mt-1 ${isOverdue ? 'text-amber-800' : 'text-gray-600'}`}>
                          {label} • {isOverdue ? 'Overdue • Due ' : 'Due '}{formatCompanyDate(dueDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 py-2">No upcoming or overdue training</p>
            )}
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
